// Auth calls against the same backend the mobile app uses. Login is
// form-urlencoded (OAuth2PasswordRequestForm) so it bypasses the JSON-oriented
// restClient and uses a raw fetch; /me and /logout go through restClient.
import { API_V1 } from 'config';
import { getJson, postJson, ApiError } from './restClient';
import { getRefreshToken, setTokens, clearTokens } from 'auth/tokenStorage';

export interface AuthUser {
  id: string;
  username: string;
  email: string;
  role: string;
  is_superuser: boolean;
  /** Admin-assigned access to the non-default analysis engines. */
  extra_engines?: boolean;
}

interface LoginResponse {
  requires_2fa?: boolean;
  two_factor_method?: string | null;
  temp_token?: string | null;
  access_token?: string | null;
  refresh_token?: string | null;
  token_type?: string;
}

/**
 * Log in with username (or email) + password. On success the access/refresh
 * pair is persisted. Throws an ApiError on bad credentials, and a plain Error
 * if the account requires 2FA (not supported by this internal tool — use a
 * non-2FA admin account).
 */
export async function login(username: string, password: string): Promise<void> {
  const res = await fetch(`${API_V1}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({ username, password }).toString(),
  });

  const text = await res.text();
  if (!res.ok) {
    let detail = text;
    try {
      const j = JSON.parse(text);
      detail = j.detail ?? j.message ?? text;
    } catch {
      /* keep raw text */
    }
    throw new ApiError(typeof detail === 'string' ? detail : JSON.stringify(detail), res.status);
  }

  const data = (text ? JSON.parse(text) : {}) as LoginResponse;
  if (data.requires_2fa) {
    throw new Error('This account requires 2FA, which the debug tool does not support. Use a non-2FA admin account.');
  }
  if (!data.access_token || !data.refresh_token) {
    throw new Error('Login did not return a token pair.');
  }
  setTokens(data.access_token, data.refresh_token);
}

/** Fetch the currently authenticated user. */
export function getMe(signal?: AbortSignal): Promise<AuthUser> {
  return getJson<AuthUser>('/users/me', signal);
}

/** Best-effort server-side logout (revokes the refresh token), then clear local tokens. */
export async function logout(): Promise<void> {
  const refreshToken = getRefreshToken();
  if (refreshToken) {
    try {
      await postJson('/auth/logout', { refresh_token: refreshToken });
    } catch {
      /* ignore — local clear below is what matters for the UI */
    }
  }
  clearTokens();
}
